import React from 'react';
import { t } from '@lingui/core/macro';

import OverflowTextModal from '@rover/components/src/OverflowTextModal/OverflowTextModal';
import { Box, Heading } from '@rover/kibble/core';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { isSitterInPetCareModesExperiment } from '../../hooks/isSitterInPetCareModesExperiment';
import { useFilteredAttributesByPetTypes } from '../../hooks/useFilteredAttributesByPetTypes';
import { sendSectionInteractionEvent } from '../../utils/sendSectionInteractionEvent';
import CommunicationAndSkills from '../CommunicationAndSkills/CommunicationAndSkills';
import CommunicationAndSkillsByPetType from '../CommunicationAndSkills/CommunicationAndSkillsByPetType';
import InfoAboutPet from '../InfoAboutPet/InfoAboutPet';

import HomeSection from './components/HomeSection';

const AboutSection = ({
  info,
  catOnlyProfile = false,
}: {
  info: EnhancedPersonSitterProfile;
  catOnlyProfile?: boolean;
}): JSX.Element => {
  const availablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(availablePets);
  const { filterAttributesByPetTypes } = useFilteredAttributesByPetTypes();
  const isPetCareModes = isSitterInPetCareModesExperiment(info);
  const sitterShortName = info.shortName;

  // we move the "accepts-only-one-client" attribute to the "Sitter can host" section
  const dogExperience =
    info.providerProfile?.hostingServicePreferences?.dogsExperienceAttributes || [];
  const homeAttributes = filterAttributesByPetTypes([
    ...(info.providerProfile?.hostingServicePreferences?.attributes || []),
    ...dogExperience.filter((e) => e.key !== 'accepts-only-one-client'),
  ]);

  return (
    <Box width="100%">
      <Heading webProps={{ responsive: false }} size="400" fontWeight="semibold" mb="0x">
        {t`About ${sitterShortName}`}
      </Heading>
      {/* Show pet-type-specific skills when the sitter being viewed is in the SSU experiment */}
      {isPetCareModes || isCatOnlyBrand ? (
        <CommunicationAndSkillsByPetType info={info} />
      ) : (
        <CommunicationAndSkills info={info} catOnlyProfile={catOnlyProfile} />
      )}
      {info.environment && (
        <Box width="100%" flexDirection="column" my="10x">
          <OverflowTextModal
            title={t`Safety, trust & environment`}
            text={info.environment}
            onReadMore={() => {
              sendSectionInteractionEvent('environment');
            }}
          />
        </Box>
      )}
      {homeAttributes.length > 0 && (
        <Box width="100%" py="10x">
          <HomeSection attributes={homeAttributes} />
        </Box>
      )}
      {info.providerProfile?.requestedDogInfo && (
        <Box width="100%" py="10x">
          <InfoAboutPet
            info={info.providerProfile?.requestedDogInfo}
            sitterShortName={info.shortName}
          />
        </Box>
      )}
      {info.providerProfile?.dogsExperience && (
        <Box width="100%" data-testid="typical-day" py="10x">
          <OverflowTextModal
            title={t`A typical day`}
            text={info.providerProfile?.dogsExperience}
            onReadMore={() => {
              sendSectionInteractionEvent('dogExperience');
            }}
          />
        </Box>
      )}
    </Box>
  );
};
export default AboutSection;
